import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import { Link, useLocation } from 'react-router';

import { Tag } from '@/shared/ui/primitives/Tag';

import { useNavigationContext } from '../../context/NavigationContext';
import { useMenuCloseTimeout } from './useMenuCloseTimeout';
import { useNavigationItemState } from './useNavigationItemState';
import { getNextOpenPaths, getSelectedHoverStyles, getSelectedStyles } from './utils';

import type { Sx } from '@/types/helpers';

export interface NavigationItemData {
  key: string;
  id?: string;
  name?: string;
  icon?: ReactNode;
  path?: string;
  onClick?: () => void;
  children?: NavigationItemData[];
  tag?: string | null;
  alignIconWithTopLevel?: boolean;
  textSx?: Sx;
}

interface NavigationItemProps {
  item: NavigationItemData;
  level?: number;
  parentPath?: string;
}

const NavigationItem = ({ item, parentPath = '', level = 0 }: NavigationItemProps) => {
  const location = useLocation();
  const { setOpenPaths, isMenuOpen, setOpenMenu, close } = useNavigationContext();
  const { fullPath, hasChildren, shouldBeOpen, isSelected } = useNavigationItemState(
    item,
    parentPath,
    level
  );
  const { scheduleClose } = useMenuCloseTimeout(() => {
    setOpenMenu(false);
  });
  const { scheduleClose: scheduleMenuClose } = useMenuCloseTimeout(close);

  const paddingLeftNumber = level === 0 ? 24 : 37 + 12 * level + 8;
  const paddingLeft = `${paddingLeftNumber}px`;

  const handleClick = useCallback(() => {
    if (level === 0 && !isMenuOpen) {
      setOpenMenu(true);
    }
    setOpenPaths(prev =>
      getNextOpenPaths(prev, level, fullPath, { isMenuOpen, isCurrentlyOpen: shouldBeOpen })
    );
  }, [level, setOpenPaths, isMenuOpen, setOpenMenu, fullPath, shouldBeOpen]);

  const handleNonRootItemClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (location.pathname === fullPath) {
        e.preventDefault();
        setOpenMenu(true);
        return;
      }

      scheduleClose();
    },
    [setOpenMenu, location.pathname, fullPath, scheduleClose]
  );

  const handleRootItemClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setOpenMenu(true);
      scheduleClose();
    },
    [setOpenMenu, scheduleClose]
  );

  const buttonProps = useMemo(() => {
    if (!hasChildren && item.onClick) {
      return {
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          item.onClick?.();
          scheduleMenuClose();
        }
      };
    }
    if (!hasChildren && item.path) {
      const onClick = level === 0 ? handleRootItemClick : handleNonRootItemClick;

      return {
        component: Link,
        to: item.path,
        onClick
      };
    }
    return { onClick: handleClick };
  }, [
    hasChildren,
    item,
    handleClick,
    scheduleMenuClose,
    level,
    handleRootItemClick,
    handleNonRootItemClick
  ]);

  return (
    <ListItem
      disablePadding
      component='div'
      sx={{
        display: 'block',
        ...(level === 0 && { '&:last-child': { mt: 'auto', mb: 0 } }),
        '.MuiListItemButton-root': shouldBeOpen
          ? {
              backgroundColor: 'gx.tileLight'
            }
          : {}
      }}
    >
      <ListItemButton
        selected={isSelected || shouldBeOpen}
        {...buttonProps}
        sx={{
          gap: '8px',
          minHeight: level === 0 ? 56 : 40,
          pl: paddingLeft,
          pr: 2,
          justifyContent: isMenuOpen ? 'initial' : 'center',
          '&.Mui-selected': getSelectedStyles(isSelected, hasChildren),
          '&.Mui-selected:hover': getSelectedHoverStyles(isSelected)
        }}
      >
        {item.icon && (
          <ListItemIcon
            sx={{
              minWidth: 0,
              justifyContent: 'center',
              position: 'relative',
              right: item.alignIconWithTopLevel && level > 0 ? paddingLeftNumber - 24 : 0
            }}
          >
            {item.icon}
          </ListItemIcon>
        )}

        <ListItemText
          primary={
            <Box
              sx={{
                display: isMenuOpen ? 'flex' : 'none',
                alignItems: 'center',
                fontWeight: isSelected ? 600 : 500,
                gap: '8px',
                position: 'relative',
                right: item.alignIconWithTopLevel && level > 0 ? paddingLeftNumber - 24 : 0
              }}
            >
              <Typography
                noWrap
                sx={{
                  fontSize: level > 0 ? '14px' : '16px',
                  display: 'inline-flex',
                  overflow: 'hidden',
                  ...item.textSx
                }}
              >
                {item.name}
              </Typography>
              {item.tag && (
                <Box
                  sx={{
                    marginLeft: level === 0 ? '16px' : 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Tag label={item.tag} />
                </Box>
              )}

              {hasChildren &&
                isMenuOpen &&
                (shouldBeOpen ? (
                  <ExpandLess sx={{ mb: '-3px' }} />
                ) : (
                  <ExpandMore sx={{ mb: '-3px' }} />
                ))}
            </Box>
          }
        />
      </ListItemButton>

      {isMenuOpen && hasChildren && (
        <Collapse
          unmountOnExit
          in={shouldBeOpen}
          timeout='auto'
        >
          <List
            disablePadding
            component='div'
          >
            {item.children?.map(childItem => (
              <NavigationItem
                key={childItem.key}
                item={childItem}
                level={level + 1}
                parentPath={fullPath}
              />
            ))}
          </List>
        </Collapse>
      )}
    </ListItem>
  );
};

export default NavigationItem;
